import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Panel, PanelBody, PanelHeader } from "../../util/panel";
import ManageRequisitionDetail from "./components/ManageRequisitionDetail";
import dayjs from "dayjs";
import ManageProductTable from "./components/ManageProductTable";
import {
  Breadcrumb,
  Button,
  Col,
  Input,
  notification,
  Row,
  Spin,
  Typography,
} from "antd";
import ManageModal from "./components/ManageModal";
import { axios } from "../../../utils/useAxios";
import { getUser } from "../../../utils";
import { Link } from "react-router-dom/cjs/react-router-dom.min";

const { TextArea } = Input;
const { Text } = Typography;

const Manage = ({ match, history }) => {
  const { document_no, process: process_params } = match.params;
  const [isNew, setIsNew] = useState();
  const [visibleProductModal, setVisibleProductModal] = useState(false);
  const [detail, setDetail] = useState({
    data: {},
    product_list: [],
    reason: null,
    status: "",
  });
  const [action, setAction] = useState({
    approve: false,
    edit: true,
    reject: false,
  });
  const [disableProduct, setDisableProduct] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const user = useMemo(() => getUser(), []);
  const onDeleteProduct = (product_code) => {
    setDetail((cur) => ({
      ...cur,
      product_list: cur?.product_list.filter(
        (e) => e.product_code !== product_code,
      ),
    }));
    setDisableProduct((prev) =>
      prev
        .split(",")
        .filter((code) => code !== product_code)
        .join(","),
    );
  };
  const onSaveDocument = async (process) => {
    setLoading(true);
    try {
      let response = null;
      if (isNew) {
        response = await axios.post(`/requisition-branch/${process}`, detail);
        history.push({
          pathname: `/requisition-branch/draft`,
        });
      } else {
        response = await axios.post(
          `/requisition-branch/${process}/${document_no}`,
          detail,
        );
      }
      notification.success({
        message: response?.data?.message,
        placement: "topRight",
      });
      if (process_params === "draft") {
        history.push({
          pathname: `/requisition-branch/${process}`,
        });
      }
    } catch (err) {
      notification.error({
        message: err?.response?.data?.message,
        placement: "topRight",
      });
    } finally {
      setIsEdit(false);
      setLoading(false);
      fetchData();
    }
  };
  const fetchData = useCallback(async () => {
    setLoading(true);
    setIsNew(document_no === "new");
    const { name: user_name, user_code, branch_name, branch_code } = user || {};
    if (document_no === "new") {
      setDetail((cur) => ({
        ...cur,
        data: {
          user_name,
          user_code,
          branch_name,
          branch_code,
          date_request: dayjs().format("DD/MM/YYYY"),
        },
      }));
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `/requisition-branch/${process_params}/${document_no}`,
        { params: { branch_code: branch_code } },
      );
      setDetail({
        data: {
          user_name: data?.detail?.user_name,
          user_code: data?.detail?.user_code,
          branch_name: data?.detail?.branch_name,
          date_request: dayjs(data?.detail?.document_date).format("DD/MM/YYYY"),
        },
        product_list: data?.product_list,
        reason: data?.detail?.remark,
        status: data?.detail?.status,
      });
      setAction(data?.action);
      setDisableProduct(
        data?.product_list.map((e) => e.product_code).join(","),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [document_no, process_params, user]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Spin spinning={loading}>
      <Breadcrumb className="mb-2">
        <Breadcrumb.Item>
          <Link to="/">หน้าหลัก</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/requisition-branch/${process_params}`}>
            เบิกสินค้าสาขา
          </Link>
        </Breadcrumb.Item>
      </Breadcrumb>
      <Panel>
        <PanelHeader>เบิกสินค้าสาขา</PanelHeader>
        <PanelBody>
          <ManageRequisitionDetail
            detail={detail?.data}
            setVisibleProductModal={(val) => setVisibleProductModal(val)}
            isEdit={isNew || isEdit}
          />
          <ManageProductTable
            dataSource={detail?.product_list}
            setDataSource={setDetail}
            onDeleteProduct={onDeleteProduct}
            isEdit={isNew || isEdit}
          />
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Col span={3} className="text-right">
                <Text strong>หมายเหตุ : </Text>
              </Col>
              <Col span={21}>
                <TextArea
                  rows={4}
                  value={detail.reason ?? ""}
                  disabled={!(isNew || isEdit)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDetail((prev) => ({ ...prev, reason: value }));
                  }}
                />
              </Col>
            </Col>
          </Row>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              marginTop: 20,
            }}
          >
            {process_params === "draft" ? (
              <>
                {isNew ? (
                  <>
                    <Button
                      onClick={() => {
                        if (isEdit) {
                          setIsEdit(false);
                          fetchData();
                          return;
                        }
                        history.push({
                          pathname: `/requisition-branch/${process_params}`,
                        });
                      }}
                    >
                      ยกเลิก
                    </Button>
                    <Button
                      type="primary"
                      ghost
                      onClick={() => onSaveDocument("draft")}
                    >
                      บันทึกร่าง
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => onSaveDocument("pending")}
                    >
                      ส่งคำขอ
                    </Button>
                  </>
                ) : (
                  <>
                    {!isNew && !isEdit && (
                      <Button
                        type="primary"
                        ghost
                        onClick={() => setIsEdit(true)}
                      >
                        แก้ไข
                      </Button>
                    )}
                    {isEdit && (
                      <>
                        <Button
                          type="danger"
                          ghost
                          onClick={() => {
                            setIsEdit(false);
                            fetchData();
                          }}
                        >
                          ยกเลิก
                        </Button>
                        <Button
                          type="primary"
                          ghost
                          onClick={() => onSaveDocument("draft")}
                        >
                          บันทึกร่าง
                        </Button>
                      </>
                    )}
                    <Button
                      type="primary"
                      onClick={() => onSaveDocument("pending")}
                    >
                      {isEdit ? "บันทึกและส่งคำขอ" : "ส่งคำขอ"}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                {!isEdit ? (
                  action.edit && (
                    <Button
                      type="primary"
                      ghost
                      onClick={() => setIsEdit(true)}
                    >
                      แก้ไข
                    </Button>
                  )
                ) : (
                  <>
                    <Button
                      type="danger"
                      ghost
                      onClick={() => {
                        setIsEdit(false);
                        fetchData();
                      }}
                    >
                      ยกเลิก
                    </Button>
                    {action.edit && (
                      <Button
                        type="primary"
                        ghost
                        onClick={() => onSaveDocument("pending")}
                      >
                        บันทึก
                      </Button>
                    )}
                    {action.approve && (
                      <Button
                        type="primary"
                        onClick={() => onSaveDocument("save-approve")}
                      >
                        บันทึกและอนุมัติ
                      </Button>
                    )}
                  </>
                )}
                {action.approve && !isEdit && (
                  <Button
                    type="danger"
                    onClick={() => onSaveDocument("reject")}
                  >
                    ไม่อนุมัติ
                  </Button>
                )}
                {action.reject && !isEdit && (
                  <Button
                    type="primary"
                    onClick={() => onSaveDocument("approve")}
                  >
                    อนุมัติ
                  </Button>
                )}
              </>
            )}
          </div>
        </PanelBody>
        <ManageModal
          visible={visibleProductModal}
          onCancel={() => setVisibleProductModal(false)}
          setDetail={(val) =>
            setDetail((prev) => ({
              ...prev,
              product_list: prev?.product_list.concat(val),
            }))
          }
          disableProduct={disableProduct}
          setDisableProduct={setDisableProduct}
        />
      </Panel>
    </Spin>
  );
};

export default Manage;
